import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { TenantProvider } from '@/components/tenant/tenant-context'
import { EntryScreen, EntryTitle, FullPageStatus, SignedInFooter } from '@/components/visitone/entry-screen'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { ProductMark } from '@/components/visitone/product-mark'
import { TenantPicker } from '@/components/visitone/tenant-picker'
import { useT } from '@/components/visitone/visitone-provider'
import { useTenants } from '@/hooks/use-tenants'
import { signOut } from '@/lib/auth/session'
import { AccessRequestError, fetchAccess } from '@/lib/bff/access'
import type { Tenant } from '@/lib/bff/tenants'
import { resolveTenant } from '@/lib/tenant/resolve-tenant'
import {
  clearHeldTenant,
  discardIdleRestore,
  openTenant,
  readHeldTenant,
  readIdleRestore,
} from '@/lib/tenant/tenant-session'

type Session = { sub: string; sid: string }

type GateState =
  | { kind: 'resolving' }
  | { kind: 'error' }
  | { kind: 'welcome' }
  | { kind: 'choose'; lostIdle: boolean }
  | { kind: 'opening'; tenant: Tenant }
  | { kind: 'open'; tenant: Tenant; announce: boolean; seq: number }

/*
 * Establishes the current tenant before any tenant-scoped provider, hook or
 * route mounts (VIS-326 AC 3, AC 4). Sits between AuthGate and the shell.
 * Every way of opening a tenant commits the held tenant first (`openTenant`).
 */
export function TenantGate({ children }: { children: React.ReactNode }) {
  const t = useT()
  const navigate = useNavigate()
  const { user, logout, getIdTokenClaims, getAccessTokenSilently } = useAuth0()
  const { tenants, error, refresh } = useTenants()
  const [session, setSession] = React.useState<Session | null>(null)
  const [gate, setGate] = React.useState<GateState>({ kind: 'resolving' })
  const [retrying, setRetrying] = React.useState(false)
  const seq = React.useRef(0)

  const userSub = user?.sub
  React.useEffect(() => {
    let cancelled = false
    void Promise.resolve(getIdTokenClaims())
      .catch(() => undefined)
      .then((claims) => {
        if (cancelled) return
        const sub = claims?.sub ?? userSub ?? ''
        const sid = typeof claims?.sid === 'string' ? claims.sid : ''
        setSession({ sub, sid })
      })
    return () => {
      cancelled = true
    }
  }, [getIdTokenClaims, userSub])

  const open = React.useCallback(
    (tenant: Tenant, fromHeld: boolean, current: Session) => {
      openTenant({ ...current, tenantId: tenant.id })
      seq.current += 1
      if (fromHeld) setGate({ kind: 'open', tenant, announce: false, seq: seq.current })
      else setGate({ kind: 'opening', tenant })
    },
    [],
  )

  const resolvable = gate.kind === 'resolving' || gate.kind === 'error'
  React.useEffect(() => {
    if (!resolvable || retrying || !session) return
    if (error && !tenants) {
      setGate({ kind: 'error' })
      return
    }
    if (!tenants) return

    const heldTenantId = readHeldTenant(session.sub, session.sid)
    const idleTenantId = readIdleRestore(session.sub)
    const result = resolveTenant({ tenants, heldTenantId, idleTenantId })
    const listed = (id: string | null) => Boolean(id && tenants.some((tenant) => tenant.id === id))

    if (heldTenantId && !listed(heldTenantId)) clearHeldTenant()
    if (idleTenantId && !listed(idleTenantId)) discardIdleRestore()

    if (result.kind === 'open') open(result.tenant, result.fromHeld, session)
    else if (result.kind === 'choose') setGate({ kind: 'choose', lostIdle: result.lostIdle })
    else if (result.kind === 'welcome') setGate({ kind: 'welcome' })
  }, [resolvable, retrying, session, tenants, error, open])

  React.useEffect(() => {
    if (gate.kind !== 'opening') return
    navigate('/', { replace: true })
    setGate({ kind: 'open', tenant: gate.tenant, announce: true, seq: seq.current })
  }, [gate, navigate])

  const tryAgain = React.useCallback(() => {
    setRetrying(true)
    setGate({ kind: 'resolving' })
    refresh()
      .catch(() => setGate({ kind: 'error' }))
      .finally(() => setRetrying(false))
  }, [refresh])

  const onLost = React.useCallback(() => setGate({ kind: 'resolving' }), [])
  const onLogout = React.useCallback(() => void signOut(logout), [logout])
  const loadAccess = React.useCallback(
    async (tenantId: string) => {
      const token = await getAccessTokenSilently()
      if (!token) throw new AccessRequestError(401, null, 'No access token available')
      return fetchAccess(token, tenantId)
    },
    [getAccessTokenSilently],
  )

  if (gate.kind === 'open' && session && tenants) {
    return (
      <TenantProvider
        key={gate.seq}
        initialTenant={gate.tenant}
        tenants={tenants}
        session={session}
        refresh={refresh}
        loadAccess={loadAccess}
        onLost={onLost}
        announceOnMount={gate.announce}
      >
        {children}
      </TenantProvider>
    )
  }

  if (gate.kind === 'opening' || gate.kind === 'open') {
    return <FullPageStatus text={t('core.tenant.opening', { tenant: gate.tenant.name })} />
  }

  const footer = <SignedInFooter email={user?.email ?? user?.name} onLogout={onLogout} />

  if (gate.kind === 'error') {
    return (
      <EntryScreen wide>
        <ProductMark />
        <OperationFeedback
          state="error"
          title={t('core.tenant.loadErrorTitle')}
          pageHeading
          detail={t('core.tenant.loadErrorBody')}
          onRetry={tryAgain}
        />
        {footer}
      </EntryScreen>
    )
  }

  if (gate.kind === 'welcome') {
    return (
      <EntryScreen>
        <ProductMark showName={false} className="scale-125 self-start" />
        <div className="flex flex-col gap-2">
          <EntryTitle className="text-balance">{t('core.tenant.welcomeTitle')}</EntryTitle>
          <p className="text-pretty text-sm text-fg-muted">{t('core.tenant.welcomeBody')}</p>
        </div>
        {footer}
      </EntryScreen>
    )
  }

  if (gate.kind === 'choose' && tenants && session) {
    return (
      <EntryScreen wide>
        <ProductMark />
        <div className="flex flex-col gap-2">
          <EntryTitle>{t('core.tenant.chooseTitle')}</EntryTitle>
          <p className="text-pretty text-sm text-fg-muted">
            {gate.lostIdle
              ? t('core.tenant.chooseLostSummary')
              : t('core.tenant.chooseSummary', { count: tenants.length })}
          </p>
        </div>
        <TenantPicker tenants={tenants} onChoose={(tenant) => open(tenant as Tenant, false, session)} />
        {footer}
      </EntryScreen>
    )
  }

  return <FullPageStatus text={t('core.tenant.loading')} />
}
